// ui/presenters/auditLearningRowPresenter.js
import { i18n } from 'boot/i18n';

import { LEARNING_SOURCES, LEARNING_STATUSES } from '../../domain/constants/auditLearnings';

/** @typedef {import('../../domain/entities/AuditLearning.types').AuditLearning} AuditLearning */

/**
 * Presenter: turns a stored learning into the row the Learning Moderations table renders.
 *
 * IT EXISTS BECAUSE THE ROWS STOPPED MAPPING ONTO FIELDS. Three of the four columns are now derived
 * rather than stored — status reads two fields plus a sibling document, scope reads a list and reports a
 * count, and provenance is the one thing still taken verbatim — so the formatting had to move somewhere
 * testable rather than live inside column `format:` callbacks.
 *
 * STATUS IS FOUR LABELS OVER TWO FIELDS, and the fourth only exists relative to another row. `status`
 * says whether this learning is applied; `parentId` and the sibling draft say what it is to the learning
 * next to it. Calling a revision draft simply "Draft" would hide that saving it replaces something.
 *
 * SCOPE HAS NO STORED VALUE. A learning naming no template applies to the whole organization, and that
 * is what the column says. One consequence is deliberate: a learning the thumbs-down flow scoped to
 * template-less activities of one type also names no template, so it reads as "Organization" while the
 * auditor still applies it more narrowly. Saving any edit to it writes real organization scope and closes
 * the gap; re-scoping every such learning up front is what HR-4763 rules out.
 */

/**
 * @param {AuditLearning} learning
 * @return {string}
 */
function statusLabel(learning) {
  if (learning.status === LEARNING_STATUSES.DRAFT) {
    return i18n.t('noteAuditor.learningsModeration.statuses.draft');
  }

  return learning.draftRevisionId
    ? i18n.t('noteAuditor.learningsModeration.statuses.draftRevisionOpen')
    : i18n.t('noteAuditor.learningsModeration.statuses.active');
}

/**
 * @param {AuditLearning} learning
 * @return {string}
 */
function sourceLabel(learning) {
  return learning.source === LEARNING_SOURCES.MANUAL
    ? i18n.t('noteAuditor.learningsModeration.sources.manual')
    : i18n.t('noteAuditor.learningsModeration.sources.fromFeedback');
}

/**
 * @param {AuditLearning} learning
 * @return {string}
 */
function appliesToLabel(learning) {
  const names = learning.sessionNoteTemplateNames ?? [];

  if (names.length === 0) {
    return i18n.t('noteAuditor.learningsModeration.appliesTo.organization');
  }

  if (names.length === 1) {
    return names[0];
  }

  return i18n.t('noteAuditor.learningsModeration.appliesTo.templateCount', { count: names.length });
}

/**
 * @param {AuditLearning} learning
 * @return {string|null} The full list, only when the label had to count them instead of naming them
 */
function appliesToTooltip(learning) {
  const names = learning.sessionNoteTemplateNames ?? [];

  return names.length > 1
    ? names.join('\n')
    : null;
}

/**
 * @param {AuditLearning} learning
 * @return {{
 *   id: string,
 *   learning: string,
 *   statusLabel: string,
 *   isDraft: boolean,
 *   isRevision: boolean,
 *   hasOpenDraftRevision: boolean,
 *   sourceLabel: string,
 *   appliesToLabel: string,
 *   appliesToTooltip: string|null,
 *   editTargetId: string,
 * }}
 */
// eslint-disable-next-line import/prefer-default-export
export function presentAuditLearningRow(learning) {
  return {
    id: learning.id,
    learning: learning.learning,
    statusLabel: statusLabel(learning),
    isDraft: learning.status === LEARNING_STATUSES.DRAFT,
    isRevision: learning.status === LEARNING_STATUSES.DRAFT && !!learning.parentId,
    hasOpenDraftRevision: learning.status !== LEARNING_STATUSES.DRAFT && !!learning.draftRevisionId,
    sourceLabel: sourceLabel(learning),
    appliesToLabel: appliesToLabel(learning),
    appliesToTooltip: appliesToTooltip(learning),
    // One draft per active learning. Edit on an active learning that already has a revision open goes to
    // that revision, so a second one can never be started.
    editTargetId: learning.draftRevisionId ?? learning.id,
  };
} // presentAuditLearningRow()
